import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Palette } from '../../constants/palette';
export const CHECKBOX_SIZES = {
    s: {
        container: 16,
        innerCircle: 8,
    },
    m: {
        container: 24,
        innerCircle: 14,
    },
    l: {
        container: 40,
        innerCircle: 22,
    },
};
export const RADIO_COLORS = {
    green: {
        selected: Palette.goGreen,
        notSelected: Palette.gray80,
        background: Palette.white,
    },
    blue: {
        selected: Palette.blue,
        notSelected: Palette.gray80,
        background: Palette.white,
    },
    white: {
        selected: Palette.blue,
        notSelected: Palette.gray80,
        background: Palette.white,
    },
};
const radioColorsDisabled = {
    green: {
        main: Palette.gray90,
        background: Palette.transparent,
    },
    blue: {
        main: Palette.gray90,
        background: Palette.transparent,
    },
    white: {
        main: Palette.gray90,
        background: Palette.transparent,
    },
};
/**
 * Return the CSS styles for equal height and size.
 *
 * @param dimension
 */
const getSquareDimensions = (dimension) => css `
  height: ${dimension}px;
  width: ${dimension}px;
`;
const RadioTag = styled.input `
  ${({ elementSize }) => getSquareDimensions(CHECKBOX_SIZES[elementSize].container)};
  flex-shrink: 0;
  align-items: center;
  appearance: none;
  background-color: ${({ color }) => RADIO_COLORS[color].background};
  border-radius: ${Number.MAX_SAFE_INTEGER}px;
  border: 2px solid ${({ color }) => RADIO_COLORS[color].notSelected};
  cursor: pointer;
  display: grid;
  justify-content: center;
  margin: 0;

  &,
  &::after {
    transition: all 0.2s ease;
    box-sizing: border-box;
  }

  &:disabled {
    border-color: ${({ color }) => radioColorsDisabled[color].main};
    background-color: ${({ color }) => radioColorsDisabled[color].background};
  }

  &:checked {
    border-color: ${({ color }) => RADIO_COLORS[color].selected};
  }
  &:disabled:checked {
    border-color: ${({ color }) => radioColorsDisabled[color].main};
  }

  &::after {
    content: '';
    ${({ elementSize }) => getSquareDimensions(CHECKBOX_SIZES[elementSize].innerCircle)};
    border-radius: ${Number.MAX_SAFE_INTEGER}px;
  }

  &:checked::after {
    background-color: ${({ color }) => RADIO_COLORS[color].selected};
  }

  &:checked:disabled::after {
    background-color: ${({ color }) => radioColorsDisabled[color].main};
  }
`;
/**
 * Radio
 *
 * @description Radio component.
 *
 * @component
 * @example
 * return (
 *   <Radio
 *     size="m"
 *     color="green"
 *     checked={false}
 *     onClick={() => { ... }}
 *   />
 * )
 */
const Radio = ({ size = 'm', color = 'green', dataCy, ...otherProps }) => {
    const cyName = useMemo(() => {
        if (dataCy) {
            return dataCy;
        }
        if (otherProps.id) {
            return otherProps.id;
        }
        if (otherProps.name) {
            return otherProps.name;
        }
        return 'radio-unknown';
    }, [dataCy, otherProps.id, otherProps.name]);
    return (_jsx(RadioTag, { type: "radio", elementSize: size, color: color, "data-cy": cyName, ...otherProps }));
};
export { Radio };
//# sourceMappingURL=data:application/json;base64,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